import { Flex, Table, TableContainer, Tbody, Th, Thead } from '@chakra-ui/react';
import TableRow from 'components/TableRow';
import UploadErrorAlert from 'containers/UploadErrorAlert';
import React from 'react';
import { FileDetailsReponse } from 'redux/services/dataLoader/types';
import { GetDocumentResponse } from 'redux/services/documentRepository/types';
import { isFileValid, getValidationStatusWithError, ValidationStatuses } from 'utils/statusHelpers';
import FileStatusRow from './components/FileStatusRow';
import PayrollMetricsRows from './components/PayrollMetricsRows';
import { usePayrollErrors } from './hooks/usePayrollErrors';

interface PayrollAdditionalDataProps {
    payrollDocumentDetail?: GetDocumentResponse;
    dataFilePayrollLocal?: FileDetailsReponse;
    payrollError: any;
    uploadPayrollFileError: any;
    payrollDocumentDetailError: any;
    presignPayrollError: any;
    uploadPayrollFileServiceError: any;
    canReloadRequestDetails: boolean;

    // APEX-1151: Validation props
    validationResults?: any;
    isValidationEnabled?: boolean;
    isValidationError?: boolean;
}

const PayrollAdditionalData = ({
    payrollDocumentDetail,
    payrollError,
    uploadPayrollFileError,
    payrollDocumentDetailError,
    presignPayrollError,
    uploadPayrollFileServiceError,
    dataFilePayrollLocal,
    canReloadRequestDetails,
    // APEX-1151: Validation props
    validationResults,
    isValidationEnabled,
    isValidationError,
}: PayrollAdditionalDataProps) => {
    const {
        apiErrors,
        fileProcessingErrors,
        hasApiErrors,
        hasDecryptError,
    } = usePayrollErrors({
        payrollError,
        uploadPayrollFileError,
        payrollDocumentDetailError,
        presignPayrollError,
        uploadPayrollFileServiceError,
        payrollDocumentDetail,
        canReloadRequestDetails,
        validationResults,
        isValidationEnabled,
    });

    // Early return for API errors
    if (hasApiErrors) {
        return <UploadErrorAlert errs={apiErrors} />;
    }

    const requestData = dataFilePayrollLocal?.data;
    const isLoading = !canReloadRequestDetails;

    // APEX-1151: Map API error to InProgress status for validation
    const validationStatus = getValidationStatusWithError(
        validationResults?.validationStatus,
        isValidationError
    );

    return (
        <Flex my="20px" flexDir="column">
            {/* Show file processing errors (anti-virus or decryption) */}
            {fileProcessingErrors.length > 0 && (
                <UploadErrorAlert errs={fileProcessingErrors} />
            )}

            <TableContainer>
                <Table size="sm">
                    <Thead>
                        <TableRow>
                            <Th>
                                <Flex gap={3} align="center">
                                    File details
                                </Flex>
                            </Th>
                            <Th></Th>
                        </TableRow>
                    </Thead>
                    <Tbody>
                        {/* Hide Anti Virus Scan row when decryption has failed (file unusable) */}
                        {!hasDecryptError && (
                            <FileStatusRow
                                label="Anti Virus Scan"
                                type="antiVirus"
                                status={payrollDocumentDetail?.data?.antiVirusStatus}
                                isLoading={isLoading}
                            />
                        )}

                        <FileStatusRow
                            label="Decryption Status"
                            type="decryption"
                            status={payrollDocumentDetail?.data?.decryptionStatus}
                            isLoading={isLoading}
                        />

                        {/* APEX-1151: File Validation row (only show if feature enabled) */}
                        {isValidationEnabled && (
                            <FileStatusRow
                                label="File Validation Status"
                                type="validation"
                                status={validationStatus}
                                isLoading={isLoading}
                            />
                        )}

                        {isFileValid(
                            payrollDocumentDetail?.data?.antiVirusStatus,
                            payrollDocumentDetail?.data?.decryptionStatus
                        ) &&
                            (!isValidationEnabled || validationStatus !== ValidationStatuses.FAILED) && (
                            <PayrollMetricsRows
                                dataFilePayrollLocal={dataFilePayrollLocal}
                                isLoading={isLoading}
                            />
                        )}
                    </Tbody>
                </Table>
            </TableContainer>

            {/* Show data loader errors if present */}
            {requestData?.errorMessage && (
                <UploadErrorAlert errs={[requestData.errorMessage]} />
            )}
        </Flex>
    );
};

export default PayrollAdditionalData;
